import {
  convertArrayBufferToBase64String,
  convertBase64StringToArrayBuffer,
} from "../util/Utility";
import { getWebCryptoSubtle } from "./CurveUtil";

/**
 * OWASP minimum for PBKDF2-HMAC-SHA256. Single tune point for latency benchmarks.
 * Must match Java Util.PBKDF2_ITERATIONS.
 */
export const PBKDF2_ITERATIONS = 600000;

/** PBKDF2 output length in bytes (AES-256 / AEAD key size). */
export const PBKDF2_KEY_LENGTH_BYTES = 32;

const getSubtle = () => {
  const cryptoObj = globalThis.crypto || (typeof window !== "undefined" && window.crypto);
  if (!cryptoObj || !cryptoObj.subtle) {
    throw new Error("Web Crypto subtle API is not available");
  }
  return cryptoObj.subtle;
};

/**
 * Derives a PBKDF2-HMAC-SHA256 key. Returns raw 32-byte material for AES / AEAD /
 * comparison. Never log the result.
 *
 * Uses SHA-256 (not SHA-1), {@link PBKDF2_ITERATIONS} (not 1000), and full 32-byte
 * output with no Base64 truncation. Salt is Base64-decoded (unlike the old path that
 * hashed the salt string bytes). Password is UTF-8 bytes for Java parity.
 *
 * @param {String} password password or passcode/passphrase
 * @param {String} saltBase64 salt string (Base64 from generateSalt / generateBCryptSalt)
 * @returns {Promise<Uint8Array|null>} raw PBKDF2 output (32 bytes), or null if password is null
 */
export const derivePbkdf2Raw = async (password, saltBase64) => {
  if (password == null) {
    return null;
  }

  const subtle = getSubtle();
  const passwordBytes = new TextEncoder().encode(password);
  const saltBytes = convertBase64StringToArrayBuffer(saltBase64);

  const passcodeAsKey = await subtle.importKey(
    "raw",
    passwordBytes,
    { name: "PBKDF2" },
    false,
    ["deriveBits"],
  );

  const hashAsArrayBuffer = await subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: saltBytes,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    passcodeAsKey,
    PBKDF2_KEY_LENGTH_BYTES * 8,
  );

  return new Uint8Array(hashAsArrayBuffer);
};

/**
 * Base64-encodes a raw key for DB storage or network transport only.
 * Do not pass the result into AES/AEAD as if it were the key.
 *
 * @param {Uint8Array|ArrayBuffer|null} key raw key bytes
 * @returns {String|null} Base64-encoded key, or null if key is null
 */
export const encodeKey = (key) => {
  if (key == null) {
    return null;
  }
  const buffer = key instanceof ArrayBuffer ? key : key.buffer.slice(
    key.byteOffset,
    key.byteOffset + key.byteLength
  );
  return convertArrayBufferToBase64String(buffer);
};

/**
 * Decodes a Base64-encoded key from DB or network back to raw bytes.
 *
 * @param {String|null} encoded Base64-encoded key
 * @returns {Uint8Array|null} raw key bytes, or null if encoded is null
 */
export const decodeKey = (encoded) => {
  if (encoded == null) {
    return null;
  }
  return new Uint8Array(convertBase64StringToArrayBuffer(encoded));
};

/**
 * Derives a PBKDF2 key and Base64-encodes it for validation-hash storage / network.
 *
 * @param {String} password password or passcode/passphrase
 * @param {String} saltBase64 salt string (Base64)
 * @returns {Promise<String|null>} Base64-encoded PBKDF2 key, or null if password is null
 */
export const derivePbkdf2Encoded = async (password, saltBase64) => {
  return encodeKey(await derivePbkdf2Raw(password, saltBase64));
};

/**
 * @deprecated Use derivePbkdf2Encoded for store/send, or derivePbkdf2Raw for AES.
 * Kept for public API compatibility; returns full Base64 of 32 raw bytes (no truncation).
 *
 * @param {String} passcode The passcode that will be used as a PBKDF2 key.
 * @param {String} salt The keySalt (Base64) to be used to create the hash.
 * @returns {Promise<String|null>} The PBKDF2 generated hash as Base64 string
 */
export const generatePbkdf2Hash = async (passcode, salt) => {
  return derivePbkdf2Encoded(passcode, salt);
};
